import { Icon, Paragraph, Text, UnstyledButton } from '@zillow/constellation-10';
import { IconPhotosOutline } from '@zillow/constellation-icons';
import { useContext } from 'react';
import StringConstants from '../../../../constants/StringConstants';
import { AppContext } from '../../../../contexts/AppContextProvider';
import { TooltipWrapper } from '@zg-rentals/satellite-components';
import { StyledExpiredCaption, StyledPlaceholderImage } from './StyledComponents';
import { truncatedTextStyles } from '../../../../styles/text';
import Image from 'next/image';

type Props = {
  fileName?: string | null | undefined;
  isMessageOwner: boolean;
  onClick(): void;
  shouldDarkenImage: boolean;
  shouldShowExpiredCaption?: boolean | null | undefined;
  shouldShowPlaceholder: boolean;
  size: number;
  url: string;
};

const TOOLTIP_TEXT = StringConstants.TOOLTIP.IMPERSONATION_VIEW;

const ImageThumbnail = ({
  fileName = '',
  isMessageOwner,
  onClick,
  shouldDarkenImage,
  shouldShowExpiredCaption = false,
  shouldShowPlaceholder,
  size,
  url,
}: Props): JSX.Element => {
  const { isImpersonator } = useContext(AppContext);
  const isZDocPreviewUrl = url.includes('zillowdocs') && url.includes('/image/preview');
  // zDocs API is capable of resizing images with height and width params
  // as opposed to images generated on the FE during upload
  const imageSrc = isZDocPreviewUrl ? `${url}&height=${size}&width=${size}` : url;

  return (
    <>
      <TooltipWrapper tooltipPlacement="top-start" tooltipText={TOOLTIP_TEXT} wrapInTooltip={isImpersonator}>
        <UnstyledButton
          data-testid="image-thumbnail-btn"
          onClick={onClick}
          aria-label="View image attachment full screen"
          aria-disabled={shouldShowPlaceholder}
          disabled={shouldShowPlaceholder}
          css={{
            width: `${size}px`,
            height: shouldShowExpiredCaption ? `${size / 2}px` : `${size}px`,
            cursor: 'pointer',
          }}
        >
          {shouldShowPlaceholder ? (
            <StyledPlaceholderImage
              data-testid="image-thumbnail-placeholder"
              alignItems="center"
              display="flex"
              justifyContent="center"
              $height={size}
              $width={size}
              $shouldShowExpiredCaption={shouldShowExpiredCaption}
              $isMessageOwner={isMessageOwner}
            >
              <Icon size="lg" css={{ color: 'border.action.neutral.default' }}>
                <IconPhotosOutline />
              </Icon>
            </StyledPlaceholderImage>
          ) : (
            <Image
              data-testid="image-thumbnail"
              alt={`Image attachment thumbnail for ${fileName}`}
              src={imageSrc}
              height={size}
              width={size}
              style={{
                borderRadius: '4px',
                objectFit: 'cover',
                filter: shouldDarkenImage ? 'brightness(45%)' : 'none',
              }}
            />
          )}
        </UnstyledButton>
      </TooltipWrapper>

      {shouldShowExpiredCaption && (
        <StyledExpiredCaption data-testid="image-expired-caption" $isMessageOwner={isMessageOwner}>
          <Paragraph textStyle="body-xs" css={{ ...truncatedTextStyles, maxWidth: '120px', color: 'text.subtle' }}>
            {fileName || 'This file'}&nbsp;
          </Paragraph>
          <Text textStyle="body-xs" css={{ color: 'text.subtle' }}>
            has expired and can't be viewed.
          </Text>
        </StyledExpiredCaption>
      )}
    </>
  );
};

export default ImageThumbnail;
